/* eslint-disable max-len */
import { useApolloClient } from '@apollo/client';
import { Button, Card, Col, Form, message, Row, Space } from 'antd';
import { Formik } from 'formik';
import { omit } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type PorschePaymentModuleSpecsFragment } from '../../../../api/fragments/PorschePaymentModuleSpecs';
import { type PorschePaymentSettingsSpecFragment } from '../../../../api/fragments/PorschePaymentSettingsSpec';
import {
    UpdatePorschePaymentSettingsDocument,
    type UpdatePorschePaymentSettingsMutation,
    type UpdatePorschePaymentSettingsMutationVariables,
} from '../../../../api/mutations/updatePorschePaymentSettings';
import { type PorschePaymentUpdateSettingsInput } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import InputNumberField from '../../../../components/fields/InputNumberField';
import PasswordField from '../../../../components/fields/PasswordField';
import SelectField from '../../../../components/fields/SelectField';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import { usePorschePaymentEnvironmentOptions } from './AddPorschePaymentSettingsForm';
import validator from './validators';

export type FormValues = PorschePaymentUpdateSettingsInput;

export type UpdatePorschePaymentSettingInnerPageProps = {
    module: PorschePaymentModuleSpecsFragment;
    paymentSettings: PorschePaymentSettingsSpecFragment & {
        secretKey?: string;
        webhookKey?: string;
    };
};

const colSpan = { lg: 8, md: 12, xs: 24 };

const UpdatePorschePaymentSettingInnerPage = ({
    module,
    paymentSettings,
}: UpdatePorschePaymentSettingInnerPageProps) => {
    const moduleId = module.id;
    const paymentSettingsId = paymentSettings.id;

    const { t } = useTranslation(['porschePaymentModuleDetails']);
    const apolloClient = useApolloClient();
    const navigate = useNavigate();

    const initialValues = useMemo(
        (): FormValues => ({
            ...omit(['id', 'paymentModuleId'], paymentSettings),
        }),
        [paymentSettings]
    );

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('porschePaymentModuleDetails:paymentSettingsDrawer.messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdatePorschePaymentSettingsMutation, UpdatePorschePaymentSettingsMutationVariables>({
                    mutation: UpdatePorschePaymentSettingsDocument,
                    variables: { moduleId, id: paymentSettingsId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('porschePaymentModuleDetails:paymentSettingsDrawer.messages.updateSuccess'),
            });

            navigate(`/admin/system/modules/${moduleId}`);
        },
        [t, apolloClient, moduleId, paymentSettingsId, navigate]
    );

    const environmentOptions = usePorschePaymentEnvironmentOptions();

    const validate = useValidator(validator(false));

    return (
        <ConsolePageWithHeader
            footer={[
                <Button key="submit" form="paymentSettingsForm" htmlType="submit" type="primary">
                    {t('porschePaymentModuleDetails:paymentSettingsDrawer.updateButton')}
                </Button>,
            ]}
            onBack={() => navigate(`/admin/system/modules/${moduleId}`)}
            title={t('porschePaymentModuleDetails:paymentSettingsDrawer.title.update')}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form
                        id="paymentSettingsForm"
                        layout="vertical"
                        name="paymentSettingsForm"
                        onSubmitCapture={handleSubmit}
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Card title={t('porschePaymentModuleDetails:dividers.mainDetails')}>
                                <Row gutter={14}>
                                    <Col {...colSpan}>
                                        <InputField
                                            {...t(
                                                'porschePaymentModuleDetails:paymentSettingsDrawer.fields.displayName',
                                                {
                                                    returnObjects: true,
                                                }
                                            )}
                                            name="displayName"
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <InputField
                                            {...t('porschePaymentModuleDetails:paymentSettingsDrawer.fields.currency', {
                                                returnObjects: true,
                                            })}
                                            name="currency"
                                        />
                                    </Col>
                                    <Col {...colSpan}>
                                        <InputNumberField
                                            {...t('porschePaymentModuleDetails:paymentSettingsDrawer.fields.amount', {
                                                returnObjects: true,
                                            })}
                                            name="amount"
                                        />
                                    </Col>
                                </Row>
                            </Card>
                            <Card title={t('porschePaymentModuleDetails:dividers.paymentSettings')}>
                                <InputField
                                    {...t('porschePaymentModuleDetails:paymentSettingsDrawer.fields.apiKey', {
                                        returnObjects: true,
                                    })}
                                    name="apiKey"
                                />
                                <PasswordField
                                    {...t('porschePaymentModuleDetails:paymentSettingsDrawer.fields.secretKey', {
                                        returnObjects: true,
                                    })}
                                    name="secretKey"
                                />
                                <SelectField
                                    {...t('porschePaymentModuleDetails:paymentSettingsDrawer.fields.environment', {
                                        returnObjects: true,
                                    })}
                                    name="environment"
                                    options={environmentOptions}
                                    showSearch
                                />
                                <InputField
                                    {...t('porschePaymentModuleDetails:paymentSettingsDrawer.fields.apiEndpoint', {
                                        returnObjects: true,
                                    })}
                                    name="apiEndpoint"
                                />
                                <InputField
                                    {...t('porschePaymentModuleDetails:paymentSettingsDrawer.fields.loginEndpoint', {
                                        returnObjects: true,
                                    })}
                                    name="loginEndpoint"
                                />
                                <InputField
                                    {...t('porschePaymentModuleDetails:paymentSettingsDrawer.fields.widgetUrl', {
                                        returnObjects: true,
                                    })}
                                    name="widgetUrl"
                                />
                                <PasswordField
                                    {...t('porschePaymentModuleDetails:paymentSettingsDrawer.fields.webhookKey', {
                                        returnObjects: true,
                                    })}
                                    name="webhookKey"
                                />
                                <InputField
                                    {...t(
                                        'porschePaymentModuleDetails:paymentSettingsDrawer.fields.endpointApiVersion',
                                        {
                                            returnObjects: true,
                                        }
                                    )}
                                    name="endpointApiVersion"
                                />
                            </Card>
                        </Space>
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default UpdatePorschePaymentSettingInnerPage;
